import * as React from 'react';
import { TestRun, TestRunStatus } from '../../types';
import { antGrey } from '../styles/globalStyles';

import Progress from 'antd/lib/progress';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';

const DONUT_SIZE = 120;
const DONUT_THICKNESS = 8;
const STATUS_BAR_WIDTH = 200;
// antd's default line height; below it the rounded ends get clipped flat
const STATUS_BAR_HEIGHT = 8;
const MIN_NON_EMPTY_STATUS_BAR_PERCENT = (STATUS_BAR_HEIGHT / STATUS_BAR_WIDTH) * 100;

const statusBarPercent = (count: number, largestStatusCount: number): number => {
    if (count === 0 || largestStatusCount === 0) {
        return 0;
    }
    return Math.max((count / largestStatusCount) * 100, MIN_NON_EMPTY_STATUS_BAR_PERCENT);
};

const donutBackground = (statuses: TestRunStatus[]): string => {
    let start = 0;
    const segments = statuses
        .filter((status) => status.fraction > 0)
        .map((status) => {
            const end = start + status.fraction * 100;
            const segment = `#${status.color} ${start}% ${end}%`;
            start = end;
            return segment;
        });
    return segments.length > 0 ? `conic-gradient(${segments.join(', ')})` : antGrey.grey4;
};

const RegressionTestingCharts = (props: TestRun): JSX.Element => {
    const { completionFraction, statuses } = props;
    const largestStatusCount = Math.max(0, ...statuses.map((status) => status.count));

    return (
        <div style={{ marginTop: 12, marginBottom: 12 }}>
            <Space size="large">
                <div style={{ position: 'relative', width: DONUT_SIZE, height: DONUT_SIZE }}>
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            borderRadius: '50%',
                            background: donutBackground(statuses),
                            mask: `radial-gradient(farthest-side, transparent calc(100% - ${DONUT_THICKNESS}px), #000 calc(100% - ${DONUT_THICKNESS}px))`,
                        }}
                    />
                    <Space
                        direction="vertical"
                        align="center"
                        size={0}
                        style={{ position: 'absolute', inset: 0, justifyContent: 'center' }}
                    >
                        <Text style={{ fontSize: 22 }}>{parseFloat((completionFraction * 100).toFixed(0))}%</Text>
                        <Text
                            type="secondary"
                            style={{ textTransform: 'uppercase', fontSize: 9, letterSpacing: '0.1em' }}
                        >
                            completed
                        </Text>
                    </Space>
                </div>
                <div className="regression-testing-progress-bars">
                    <Space direction="vertical" size={0}>
                        {statuses.map((status, key) => (
                            <Space key={`status-${key}`}>
                                <Text style={{ minWidth: 30, textAlign: 'right', display: 'inline-block' }}>
                                    {status.count}
                                </Text>
                                <Progress
                                    className="bordered"
                                    percent={statusBarPercent(status.count, largestStatusCount)}
                                    strokeColor={`#${status.color}`}
                                    showInfo={false}
                                    size={[STATUS_BAR_WIDTH, STATUS_BAR_HEIGHT]}
                                    style={{ marginBottom: 0 }}
                                />
                                <Text type="secondary">{status.displayableName}</Text>
                            </Space>
                        ))}
                    </Space>
                </div>
            </Space>
        </div>
    );
};

export default RegressionTestingCharts;
